/**
 * `?next=` validation.
 *
 * An open redirect on a login screen is a real vulnerability, and this parameter is
 * reachable from an email link. Applied at BOTH ends: the guard only ever WRITES our own
 * `location.pathname + location.search`, and every READ passes through here.
 *
 * Rejection is silent — falling back to "/" rather than showing an error, because an
 * attacker learns nothing from silence.
 */
export function safeNext(raw: string | null | undefined): string {
  if (!raw) return "/";

  // Decode once so a percent-encoded "//evil.test" cannot slip past the prefix checks.
  // decodeURIComponent throws on a malformed sequence, which is itself a rejection.
  let value: string;
  try {
    value = decodeURIComponent(raw);
  } catch {
    return "/";
  }

  if (!value.startsWith("/")) return "/"; // absolute URLs, mailto:, javascript:, data:
  if (value.startsWith("//")) return "/"; // protocol-relative -> another origin
  if (value.startsWith("/\\")) return "/"; // backslash variants some parsers normalise to //
  if (value.includes("\\")) return "/"; // any other backslash — nothing legitimate needs one
  // Control characters, including the newline/tab tricks used to split a header or confuse
  // a URL parser.
  if (/[\u0000-\u001f\u007f]/.test(value)) return "/";

  return value;
}

/** The only thing a guard is allowed to write into `next`. */
export function currentPath(location: { pathname: string; search: string }): string {
  return `${location.pathname}${location.search}`;
}
